import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { isSuperAdmin } from "@/types";
import type { AuthUser, HiringMatrix, Position } from "@/types";
import { api } from "@/lib/api";
import { PositionMatrixGroup } from "../shared/common/PositionMatrixGroup";
import { NewMatrixForm } from "./matrix/NewMatrixForm";

interface MatrixManagerProps {
  positions: Position[];
  currentUser?: AuthUser;
  onPositionsChange: () => void;
}

export function MatrixManager({ positions, currentUser, onPositionsChange }: MatrixManagerProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const pathParts = location.pathname.split("/").filter(Boolean);
  const editingId = pathParts[0] === "matrices" && pathParts[2] === "edit" ? parseInt(pathParts[1], 10) || null : null;

  const [matrices, setMatrices] = useState<HiringMatrix[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterPositionId, setFilterPositionId] = useState<string>("all");
  const [filterOrgId, setFilterOrgId] = useState<number | null>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [open, setOpen] = useState(false);

  const { data: orgsData } = useQuery({
    queryKey: ["organizations"],
    queryFn: () => api.listOrganizations(),
    enabled: isSuperAdmin(currentUser),
  });
  const orgs = orgsData?.organizations ?? [];

  const refresh = async () => {
    try {
      const { matrices } = await api.listMatrices();
      setMatrices(matrices);
    } catch { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { refresh(); }, []);

  const remove = async (id: number) => {
    if (!confirm("Delete this hiring matrix and all associated candidates?")) return;
    await api.deleteMatrix(id);
    refresh();
  };

  const removePosition = async (id: number) => {
    if (!confirm("Delete this position, all its matrices, and all its candidates?")) return;
    await api.deletePosition(id);
    onPositionsChange();
    refresh();
  };

  const filteredMatrices = matrices.filter((m) => {
    const q = search.toLowerCase().trim();
    if (q) {
      const nameMatch = m.name.toLowerCase().includes(q);
      const criteriaMatch = m.criteria.some((c) => c.name.toLowerCase().includes(q) || c.requirement.toLowerCase().includes(q));
      const posTitle = (m.position_title ?? "").toLowerCase().includes(q);
      if (!nameMatch && !criteriaMatch && !posTitle) return false;
    }
    if (filterPositionId !== "all" && m.position_id !== Number(filterPositionId)) return false;
    return true;
  });

  const grouped = positions
    .filter((p) => filterOrgId === null || p.org_id === filterOrgId)
    .map((p) => ({ position: p, matrices: filteredMatrices.filter((m) => m.position_id === p.id) }))
    .filter((g) => g.matrices.length > 0 || (filterPositionId === "all" && search === "" && filterOrgId === null));

  const activeFilterCount = (filterPositionId !== "all" ? 1 : 0) + (filterOrgId !== null ? 1 : 0);
  const hasActiveFilters = search !== "" || activeFilterCount > 0;
  const clearFilters = () => { setSearch(""); setFilterPositionId("all"); setFilterOrgId(null); };

  const positionCounts: Record<number, number> = {};
  for (const m of matrices) positionCounts[m.position_id] = (positionCounts[m.position_id] || 0) + 1;

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="shrink-0 shadow-sm bg-white px-3 py-4 sm:px-6">
        <div className="mx-auto max-w-6xl">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <h2 className="text-[28px] font-bold leading-[1.43] text-near-black">Hiring Matrix</h2>
              <span className="rounded-badge bg-near-black px-2.5 py-0.5 text-xs font-semibold tabular-nums text-white">{matrices.length}</span>
            </div>
            <div className="flex flex-1 items-center justify-end gap-2 sm:max-w-lg">
              <div className="relative flex-1 sm:max-w-xs">
                <svg className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-text-secondary" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="m21 21-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607Z" />
                </svg>
                <input className="w-full rounded-btn border border-border-card bg-white py-2 pl-10 pr-3 text-sm text-near-black placeholder:text-text-secondary focus:outline-none focus:shadow-focus" placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
              <button
                onClick={() => setFiltersOpen(!filtersOpen)}
                className={["flex shrink-0 items-center gap-1.5 rounded-btn px-4 py-2 text-sm font-medium transition-colors", filtersOpen || activeFilterCount > 0 ? "bg-near-black text-white" : "bg-surface-secondary text-text-focused hover:bg-near-black hover:text-white"].join(" ")}
              >
                <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 3c2.755 0 5.455.232 8.083.678.533.09.917.556.917 1.096v1.044a2.25 2.25 0 0 1-.659 1.591l-5.432 5.432a2.25 2.25 0 0 0-.659 1.591v2.927a2.25 2.25 0 0 1-1.244 2.013L9.75 21v-6.568a2.25 2.25 0 0 0-.659-1.591L3.659 7.409A2.25 2.25 0 0 1 3 5.818V4.774c0-.54.384-1.006.917-1.096A48.32 48.32 0 0 1 12 3Z" />
                </svg>
                Filters
                {activeFilterCount > 0 && (
                  <span className="flex h-4 w-4 items-center justify-center rounded-full bg-rausch text-[10px] font-bold text-white">{activeFilterCount}</span>
                )}
              </button>
              <button
                className="shrink-0 rounded-btn bg-near-black px-6 py-2 text-sm font-medium text-white transition-colors hover:bg-rausch"
                onClick={() => { setOpen(!open); if (editingId) navigate("/matrices"); }}
              >
                {open ? "Cancel" : "+ Add"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {filtersOpen && (
        <div className="shrink-0 shadow-sm bg-white px-3 py-4 sm:px-6">
          <div className="mx-auto max-w-6xl">
            <div className="rounded-card bg-white p-5 shadow-card">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                {isSuperAdmin(currentUser) && orgs.length > 0 && (
                  <div>
                    <label className="text-[10px] font-semibold uppercase tracking-[0.32px] text-text-secondary">Organization</label>
                    <select className="mt-1 w-full rounded-btn border border-border-card bg-white px-2 py-1.5 text-xs text-near-black focus:outline-none focus:shadow-focus" value={filterOrgId ?? ""} onChange={(e) => setFilterOrgId(e.target.value ? Number(e.target.value) : null)}>
                      <option value="">All organizations</option>
                      {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
                    </select>
                  </div>
                )}
                <div>
                  <label className="text-[10px] font-semibold uppercase tracking-[0.32px] text-text-secondary">Position</label>
                  <select className="mt-1 w-full rounded-btn border border-border-card bg-white px-2 py-1.5 text-xs text-near-black focus:outline-none focus:shadow-focus" value={filterPositionId} onChange={(e) => setFilterPositionId(e.target.value)}>
                    <option value="all">All positions</option>
                    {positions.map((p) => <option key={p.id} value={p.id}>{p.title}{positionCounts[p.id] ? ` (${positionCounts[p.id]})` : ""}</option>)}
                  </select>
                </div>
              </div>
              {hasActiveFilters && (
                <div className="mt-3 flex items-center justify-between">
                  <p className="text-xs text-text-secondary">Showing {filteredMatrices.length} of {matrices.length} matrices</p>
                  <button onClick={clearFilters} className="text-xs font-medium text-rausch hover:underline">Clear filters</button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="flex-1 overflow-y-auto">
        <div className="mx-auto max-w-6xl px-3 py-4 sm:px-6 sm:py-6">
          {open && (
            <NewMatrixForm
              positions={positions}
              onPositionsChange={onPositionsChange}
              onCreated={() => { setOpen(false); refresh(); }}
              onCancel={() => setOpen(false)}
            />
          )}

          {loading && <p className="text-text-secondary">Loading…</p>}

          {!loading && grouped.length === 0 && (
            <div className="rounded-card border-2 border-dashed border-border-card bg-white p-10 text-center text-text-secondary">
              {matrices.length === 0 ? "No matrices yet. Create one to start screening." : "No matrices match the current filters."}
            </div>
          )}

          {!loading && grouped.map(({ position, matrices: posMatrices }) => (
            <PositionMatrixGroup
              key={position.id}
              position={position}
              matrices={posMatrices}
              editingId={editingId}
              isSuperAdmin={isSuperAdmin(currentUser)}
              onDeletePosition={removePosition}
              onDeleteMatrix={remove}
              onMatrixUpdated={refresh}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
